import type * as React from 'react';
import { useEffect, useId, useRef } from 'react';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { IconButton } from '../icon-button/icon-button.tsx';
import type { ChatActivityStep } from './chat-activity.tsx';
import { ChatActivityIconGlyph } from './chat-activity-icon.tsx';
import { ChatCardLink } from './chat-card.tsx';
import { chatActivityItemKey } from './chatActivityItemKey.ts';
import { containTab } from './containTab.ts';
import { followInApp } from './followInApp.ts';

export interface ChatActivityPanelProps {
  /** The step whose items are read here; `index` is the one on screen. */
  step: ChatActivityStep;
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** What kind of thing the item is, above it, e.g. "Herinnering". */
  kind: string;
  /** The link to the item's page, e.g. "Open voorbereiding". */
  openLabel: string;
  closeLabel: string;
  previousLabel: string;
  nextLabel: string;
  /** Where the reader is among the step's items, e.g. "2 van 4". */
  positionLabel: (position: number, count: number) => string;
  /** Follows a link to one of our own pages without reloading the app. */
  onNavigate?: (path: string) => void;
}

const PAGER =
  'flex cursor-pointer items-center gap-1 rounded-sm border border-line px-2.5 py-1.25 text-sm text-ink-secondary hover:bg-fill aria-disabled:cursor-default aria-disabled:opacity-40 aria-disabled:hover:bg-transparent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint';

/**
 * One item of a step in full, in a panel that slides over the chat: the whole
 * text at a readable size, when it was saved, its page, and the other items of
 * the same step one click away. It holds focus while open and hands it back to
 * the item's line when it closes.
 *
 * Drawn inside the chat drawer, which positions it, so it covers the
 * conversation and nothing beside it.
 */
export function ChatActivityPanel({
  step,
  index,
  onIndexChange,
  onClose,
  kind,
  openLabel,
  closeLabel,
  previousLabel,
  nextLabel,
  positionLabel,
  onNavigate,
}: ChatActivityPanelProps) {
  const titleId = useId();
  const panel = useRef<HTMLElement>(null);
  const items = step.detail ?? [];
  const item = items[index];
  const lineKey = useRef(chatActivityItemKey(step.id, index));

  useEffect(() => {
    lineKey.current = chatActivityItemKey(step.id, index);
  }, [step.id, index]);

  useEffect(() => {
    const before = document.activeElement;
    panel.current?.focus();
    const key = lineKey;

    return () => {
      const line = document.querySelector<HTMLElement>(
        `[data-activity-item="${CSS.escape(key.current)}"]`,
      );
      (line ?? (before instanceof HTMLElement ? before : null))?.focus();
    };
  }, []);

  if (!item) {
    return null;
  }

  const first = index === 0;
  const last = index === items.length - 1;
  const paragraphs = (item.body ?? '').split(/\n{2,}/).filter((part) => part.trim());

  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape') {
      /* Only the panel closes; the drawer under it stays open. */
      event.stopPropagation();
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key === 'Tab') {
      containTab(event);
    }
  }

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    if (!onNavigate) {
      return;
    }

    followInApp(event, onNavigate);
    if (event.defaultPrevented) {
      onClose();
    }
  }

  return (
    <>
      <div
        data-slot='chat-activity-panel-scrim'
        aria-hidden='true'
        onClick={onClose}
        className='absolute inset-0 z-40 bg-scrim/55 transition-opacity duration-200 starting:opacity-0 motion-reduce:transition-none'
      />
      <section
        ref={panel}
        data-slot='chat-activity-panel'
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        className={cn(
          'absolute inset-y-0 right-0 z-40 flex w-[86%] max-w-xl flex-col bg-surface shadow-drawer outline-none',
          'transition-transform duration-200 ease-out starting:translate-x-full motion-reduce:transition-none',
          'max-mobile:w-full',
        )}
      >
        <div className='flex shrink-0 items-center gap-2.5 border-b border-line py-3 pr-3 pl-4.5'>
          <span className='flex size-6.5 shrink-0 items-center justify-center rounded-full border border-success/22 bg-accent/10 text-success'>
            <ChatActivityIconGlyph icon={step.icon} />
          </span>
          <span className='min-w-0 truncate text-xs font-semibold tracking-wide text-ink-muted uppercase'>
            {kind}
          </span>
          <IconButton className='ml-auto' aria-label={closeLabel} onClick={onClose}>
            <X size={16} aria-hidden='true' />
          </IconButton>
        </div>

        <div
          data-slot='chat-activity-panel-body'
          className='min-h-0 flex-1 overflow-y-auto px-5.5 pt-4.5 pb-6'
        >
          <h3
            id={titleId}
            className='mb-2.5 text-lg font-semibold tracking-tight break-words text-ink'
          >
            {item.title}
          </h3>
          {paragraphs.map((paragraph, at) => (
            <p
              key={at}
              className='mb-3 flex flex-col gap-1.5 text-md leading-relaxed break-words text-ink-body'
            >
              {paragraph.split('\n').map((line, row) => (
                <span key={row}>{line}</span>
              ))}
            </p>
          ))}
          {item.meta ? (
            <p className='mt-1 border-t border-line pt-3 text-sm text-ink-muted'>
              {item.meta}
            </p>
          ) : null}
          {item.href ? (
            <ChatCardLink
              href={item.href}
              className='mt-4 inline-flex items-center gap-1 text-sm font-medium text-ink no-underline hover:underline'
            >
              {openLabel}
              <ArrowRight size={13} aria-hidden='true' />
            </ChatCardLink>
          ) : null}
          {items.length > 1 ? (
            <div className='mt-4 flex items-center gap-1.5'>
              <button
                type='button'
                aria-disabled={first}
                onClick={() => !first && onIndexChange(index - 1)}
                className={PAGER}
              >
                <ArrowLeft size={13} aria-hidden='true' />
                {previousLabel}
              </button>
              <button
                type='button'
                aria-disabled={last}
                onClick={() => !last && onIndexChange(index + 1)}
                className={PAGER}
              >
                {nextLabel}
                <ArrowRight size={13} aria-hidden='true' />
              </button>
              <span aria-live='polite' className='ml-1.5 text-sm text-ink-muted'>
                {positionLabel(index + 1, items.length)}
              </span>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}
